import { IconButton, Typography, Select, MenuItem, Box, Modal,} from "@mui/material";
import { ArrowBack, ArrowForward, CalendarMonthOutlined,} from "@mui/icons-material";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { useState } from "react";

const months = Array.from({ length: 12 }, (_, i) => ({
  label: format(new Date(2024, i, 1), "MMMM"),
  value: i,
}));

const MonthCalendar = ({ selectionRange, setSelectionRange }) => {
  const [open, setOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 9;
  const maxYear = currentYear;

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    const selectedDate = new Date(selectedYear, month, 1);
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);

    setSelectionRange({
      startDate: start,
      endDate: end,
      key: "selection",
      selectedMonth: format(selectedDate, "yyyy-MM"),
    });
    setOpen(false);
  };

  const handleSetCurrentMonth = () => {
    const today = new Date();
    const start = startOfMonth(today);
    const end = endOfMonth(today);
    setSelectedYear(today.getFullYear());
    setSelectedMonth(today.getMonth());

    setSelectionRange({
      startDate: start,
      endDate: end,
      key: "selection",
      selectedMonth: format(today, "yyyy-MM"),
    });

    setOpen(false);
  };

  const handlePrevYear = () =>
    setSelectedYear((prev) => Math.max(prev - 1, minYear));
  const handleNextYear = () =>
    setSelectedYear((prev) => Math.min(prev + 1, maxYear));
  const handlePrevMonth = () => {
    let newMonth = selectedMonth - 1;
    let newYear = selectedYear;

    if (newMonth < 0) {
      newMonth = 11;
      newYear = selectedYear - 1;
    }

    if (newYear < minYear) return;

    setSelectedMonth(newMonth);
    setSelectedYear(newYear);

    const selectedDate = new Date(newYear, newMonth, 1);
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);

    setSelectionRange({
      startDate: start,
      endDate: end,
      key: "selection",
      selectedMonth: format(selectedDate, "yyyy-MM"),
    });
  };

  const handleNextMonth = () => {
    let newMonth = selectedMonth + 1;
    let newYear = selectedYear;

    if (newMonth > 11) {
      newMonth = 0;
      newYear = selectedYear + 1;
    }

    if (newYear > maxYear) return;

    setSelectedMonth(newMonth);
    setSelectedYear(newYear);

    const selectedDate = new Date(newYear, newMonth, 1);
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);

    setSelectionRange({
      startDate: start,
      endDate: end,
      key: "selection",
      selectedMonth: format(selectedDate, "yyyy-MM"),
    });
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton onClick={handlePrevMonth}>
          <ArrowBack />
        </IconButton>
        <IconButton onClick={() => setOpen(true)}>
          <CalendarMonthOutlined />
        </IconButton>
        <Typography
          variant="body1"
          onClick={() => setOpen(true)}
          sx={{
            cursor: "pointer",
            fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
          }}
        >
          {format(selectionRange?.startDate ?? new Date(), "MMMM-yyyy")}
        </Typography>
        <IconButton onClick={handleNextMonth}>
          <ArrowForward />
        </IconButton>
      </Box>

      {/* Modal Calendar */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            backgroundColor: "var(--cell-bg-color)",
            p: 4,
            maxWidth: 600,
            margin: "100px auto",
            borderRadius: 3,
          }}
        >
          {/* Year Selector */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            mb={2}
          >
            <IconButton
              onClick={handlePrevYear}
              disabled={selectedYear === minYear}
            >
              <ArrowBack />
            </IconButton>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              size="small"
            >
              {Array.from({ length: 10 }, (_, i) => {
                const year = minYear + i;
                return (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                );
              })}
            </Select>
            <IconButton
              onClick={handleNextYear}
              disabled={selectedYear === maxYear}
            >
              <ArrowForward />
            </IconButton>
          </Box>

          {/* Months Grid */}
          <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2}>
            {months.map((month) => (
              <Box
                key={month.value}
                sx={{
                  cursor: "pointer",
                  p: 1,
                  borderRadius: 2,
                  textAlign: "center",
                  backgroundColor:
                    selectedMonth === month.value
                      ? "rgba(18, 183, 159, 1)"
                      : "var(--header-bg-color) ",
                  color: selectedMonth === month.value ? "white" : "black",
                  "&:hover": {
                    backgroundColor:
                      selectedMonth === month.value
                        ? "teal.dark"
                        : "var(--hover-bg-color)",
                  },
                }}
                onClick={() => handleMonthSelect(month.value)}
              >
                <Typography>{month.label}</Typography>
              </Box>
            ))}
          </Box>

          <Box textAlign="center" mt={3}>
            <Typography
              sx={{ cursor: "pointer", color: "primary.main" }}
              onClick={handleSetCurrentMonth}
            >
              Use Current Month
            </Typography>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default MonthCalendar;
